;globalThis["_sentryRouteManifest"] = "{\"isrRoutes\":[],\"dynamicRoutes\":[],\"staticRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.4.3";globalThis["_sentryRewritesTunnelPath"] = "/api/monitoring";import * as Sentry from '@sentry/nextjs';
import { env } from 'shared/library/env';
import { loadReplay } from 'shared/library/monitoring/load-replay';
import { ApiError } from 'shared/library/use-api';
import {
  reloadOnRouteLoadError,
  wasChunkLoadErrorRecovered,
} from '@web/library/chunk-load-recovery';
import {
  containsThirdPartyFrames,
  isBrowserExtensionApiError,
  isBrowserExtensionTimeout,
  isGoogleAdUndefinedPromiseRejection,
  isGoogleTranslatePromiseRejection,
  isMachineTranslatedDomRemovalError,
  isMapboxErrorCallbackTypeError,
} from '@web/library/sentry-noise';

if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    reloadOnRouteLoadError(event.reason);
  });
}

const WEBKIT_SIGNAL = 'window.webkit.messagehandlers';
const GTM_SIGNALS = ['googletagmanager.com', 'gtag/js'] as const;
const BROWSER_EXTENSION_SIGNALS = [
  'darkreader',
  '__firefox__',
  'window.__firefox__',
  'window.ethereum',
  'ethereum.selectedaddress',
  'object not found matching id:',
  'request timeout toolbarstatus',
  'wordsdistributor.getvalue',
  'topicsdistributor.getvalue',
  'textcheckersdistributor.getvalue',
  'availablelanguagesdistributor.getvalue',
  'availabletextchecklanguagesdistributor.getvalue',
  'dictatestatedistributor.getvalue',
  'predictiondistributor.getvalue',
  'refresh_youtube_quality_',
] as const;
const IGNORED_ERROR_NAME_PARTS = ['middleware', 'getserversideprops', '/api/monitoring'] as const;

type OriginalException = { message?: unknown; name?: unknown };

function lower(value: unknown): string {
  return String(value ?? '').toLowerCase();
}

function includesAny(texts: readonly string[], signals: readonly string[]): boolean {
  return texts.some((text) => signals.some((signal) => text.includes(signal)));
}

function isIgnoredErrorName(name: string): boolean {
  return (
    name === 'default' ||
    name.startsWith('get /_error') ||
    IGNORED_ERROR_NAME_PARTS.some((part) => name.includes(part))
  );
}

function isNoiseEvent(event: Sentry.ErrorEvent, hint: Sentry.EventHint): boolean {
  const exception = hint.originalException as OriginalException | undefined;
  const firstException = event.exception?.values?.[0];
  const exceptionType = lower(event.exception?.values?.[0]?.type);
  const exceptionValue = lower(event.exception?.values?.[0]?.value);
  const eventMessage = lower(event.message);
  const originalMessage = lower(exception?.message);
  const browserName = lower(event.contexts?.browser?.name ?? event.tags?.browser);
  const mechanism = lower(event.exception?.values?.[0]?.mechanism?.type);

  // Only inspect the actual error payload. Page-level breadcrumbs often contain
  // analytics requests, which can otherwise hide legitimate app errors.
  const errorText = [exceptionValue, eventMessage, originalMessage];

  if (errorText.some((text) => text.includes(WEBKIT_SIGNAL))) return true;
  if (includesAny(errorText, GTM_SIGNALS)) return true;
  if (includesAny(errorText, BROWSER_EXTENSION_SIGNALS)) return true;
  if (isBrowserExtensionApiError(errorText)) return true;
  if (isBrowserExtensionTimeout(errorText)) return true;
  if (
    event.exception?.values?.length === 1 &&
    firstException &&
    isGoogleTranslatePromiseRejection({
      exceptionType: firstException.type,
      exceptionValue: firstException.value,
      frames: firstException.stacktrace?.frames,
      mechanism: firstException.mechanism?.type,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    })
  ) {
    return true;
  }
  if (
    isGoogleAdUndefinedPromiseRejection({
      breadcrumbs: event.breadcrumbs,
      exceptionType,
      exceptionValue,
      mechanism,
    })
  ) {
    return true;
  }
  if (
    isMapboxErrorCallbackTypeError({
      exceptionType,
      exceptionValue,
      frames: event.exception?.values?.[0]?.stacktrace?.frames,
    })
  ) {
    return true;
  }
  if (
    isMachineTranslatedDomRemovalError({
      breadcrumbs: event.breadcrumbs,
      exceptionType,
      exceptionValue,
      frames: event.exception?.values?.[0]?.stacktrace?.frames,
      pageIsTranslated: /\btranslated-(?:ltr|rtl)\b/.test(
        globalThis.document?.documentElement.className ?? '',
      ),
    })
  ) {
    return true;
  }
  if (
    mechanism === 'auto.browser.global_handlers.onunhandledrejection' &&
    (exceptionValue === 'ba' ||
      errorText.includes('notsupportederror: the operation is not supported.'))
  ) {
    return true;
  }
  if (
    exceptionType === 'typeerror' &&
    browserName.startsWith('opera') &&
    errorText.some((text) => /^load failed \((?:media\.)?getapet\.com\)$/.test(text))
  ) {
    return true;
  }

  return false;
}

Sentry.init({
  enabled: process.env.NEXT_PUBLIC_SENTRY_ENABLED === 'true',
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  release: env.APP_VERSION,
  environment: process.env.NODE_ENV,
  enableLogs: false,
  sendDefaultPii: false,
  tracesSampleRate: 0.1,
  tunnel: '/api/monitoring',
  profileSessionSampleRate: 1.0,
  replaysOnErrorSampleRate: 1.0,
  replaysSessionSampleRate: 0,
  denyUrls: [/chrome-extension:\/\//i, /moz-extension:\/\//i],
  ignoreErrors: [
    /^Cancel rendering route$/i,
    /^Failed to read a named property 'Element' from 'Window': Blocked a frame with origin .+ from accessing a cross-origin frame\.$/i,
    /^Failed to read a named property 'Element' from 'Window': An attempt was made to break through the security policy of the user agent\.$/i,
  ],
  integrations: [
    Sentry.browserTracingIntegration({
      instrumentPageLoad: true,
      instrumentNavigation: true,
      traceFetch: false,
    }),
  ],
  beforeSend(event, hint) {
    const error = hint.originalException as ApiError | undefined;

    if (wasChunkLoadErrorRecovered(error)) {
      return null;
    }

    if (error?.name === 'ApiError' && !error.report) {
      return null;
    }

    if (isNoiseEvent(event, hint)) {
      return null;
    }

    if (
      containsThirdPartyFrames(
        event.exception?.values?.flatMap((value) => value.stacktrace?.frames ?? []) ?? [],
      )
    ) {
      return null;
    }

    if (isIgnoredErrorName(lower(error?.name))) {
      return null;
    }

    return event;
  },
});

function scheduleReplay() {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(() => void loadReplay(), { timeout: 2000 });
  } else {
    window.setTimeout(() => void loadReplay(), 2000);
  }
}

if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_SENTRY_ENABLED === 'true') {
  if (document.readyState === 'complete') {
    scheduleReplay();
  } else {
    window.addEventListener('load', scheduleReplay, { once: true });
  }
}

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
